// Demo Data for Teams & Permissions System

import type { Team, User, Permission } from '@/types/teams';

// Default Teams
export const demoTeams: Team[] = [
  {
    id: 'team_default_standard',
    name: 'Standard Users',
    icon: 'users',
    color: '#6B7280',
    description: 'Default team for all application users with full access by default',
    isDefault: true,
    canDelete: false,
    canRename: false,
    memberCount: 45,
    permissions: [
      {
        permissionId: 'perm_dashboard',
        screenKey: 'dashboard',
        granted: true,
        subPermissions: [],
      },
      {
        permissionId: 'perm_assets',
        screenKey: 'assets',
        granted: true,
        subPermissions: [
          { id: 'perm_assets_view', key: 'view_assets', granted: true },
          { id: 'perm_assets_create', key: 'create_assets', granted: true },
          { id: 'perm_assets_edit', key: 'edit_assets', granted: true },
          { id: 'perm_assets_delete', key: 'delete_assets', granted: true },
        ],
      },
      {
        permissionId: 'perm_trans_additions',
        screenKey: 'transactions_additions',
        granted: true,
        subPermissions: [
          { id: 'perm_trans_additions_view', key: 'view_additions', granted: true },
          { id: 'perm_trans_additions_create', key: 'create_additions', granted: true },
          { id: 'perm_trans_additions_edit', key: 'edit_additions', granted: true },
        ],
      },
      {
        permissionId: 'perm_trans_depreciation',
        screenKey: 'transactions_depreciation',
        granted: true,
        subPermissions: [
          { id: 'perm_trans_depreciation_view', key: 'view_depreciation', granted: true },
          { id: 'perm_trans_depreciation_create', key: 'create_depreciation', granted: true },
          { id: 'perm_trans_depreciation_edit', key: 'edit_depreciation', granted: true },
        ],
      },
      {
        permissionId: 'perm_trans_revaluation',
        screenKey: 'transactions_revaluation',
        granted: true,
        subPermissions: [
          { id: 'perm_trans_revaluation_view', key: 'view_revaluation', granted: true },
          { id: 'perm_trans_revaluation_create', key: 'create_revaluation', granted: true },
          { id: 'perm_trans_revaluation_edit', key: 'edit_revaluation', granted: true },
        ],
      },
      {
        permissionId: 'perm_trans_disposals',
        screenKey: 'transactions_disposals',
        granted: true,
        subPermissions: [
          { id: 'perm_trans_disposals_view', key: 'view_disposals', granted: true },
          { id: 'perm_trans_disposals_create', key: 'create_disposals', granted: true },
          { id: 'perm_trans_disposals_edit', key: 'edit_disposals', granted: true },
        ],
      },
      {
        permissionId: 'perm_tasks',
        screenKey: 'tasks',
        granted: true,
        subPermissions: [
          { id: 'perm_tasks_view', key: 'view_tasks', granted: true },
          { id: 'perm_tasks_create', key: 'create_tasks', granted: true },
          { id: 'perm_tasks_edit', key: 'edit_tasks', granted: true },
          { id: 'perm_tasks_assign', key: 'assign_tasks', granted: true },
          { id: 'perm_tasks_admin', key: 'task_administrator', granted: true },
        ],
      },
      {
        permissionId: 'perm_audits_auditor',
        screenKey: 'audits_auditor',
        granted: true,
        subPermissions: [
          { id: 'perm_audits_auditor_view', key: 'view_assigned_audits', granted: true },
          { id: 'perm_audits_auditor_carry_out', key: 'carry_out_audit', granted: true },
          { id: 'perm_audits_auditor_process', key: 'process_audit', granted: true },
        ],
      },
      {
        permissionId: 'perm_audits_manager',
        screenKey: 'audits_manager',
        granted: true,
        subPermissions: [
          { id: 'perm_audits_manager_view', key: 'view_all_audits', granted: true },
          { id: 'perm_audits_manager_create', key: 'create_audits', granted: true },
          { id: 'perm_audits_manager_edit', key: 'edit_audits', granted: true },
          { id: 'perm_audits_manager_flag', key: 'flag_audits', granted: true },
          { id: 'perm_audits_manager_comments', key: 'raise_comments', granted: true },
          { id: 'perm_audits_manager_work_items', key: 'raise_work_items', granted: true },
        ],
      },
      {
        permissionId: 'perm_audit_trail',
        screenKey: 'audit_trail',
        granted: true,
        subPermissions: [],
      },
      {
        permissionId: 'perm_compliance',
        screenKey: 'compliance',
        granted: true,
        subPermissions: [
          { id: 'perm_compliance_view', key: 'view_compliance', granted: true },
          { id: 'perm_compliance_manage', key: 'manage_compliance', granted: true },
        ],
      },
      {
        permissionId: 'perm_checkinout',
        screenKey: 'checkinout',
        granted: true,
        subPermissions: [
          { id: 'perm_checkinout_view', key: 'view_checkinout', granted: true },
          { id: 'perm_checkinout_manage', key: 'manage_checkinout', granted: true },
        ],
      },
      {
        permissionId: 'perm_insights',
        screenKey: 'insights',
        granted: true,
        subPermissions: [
          { id: 'perm_insights_view', key: 'view_insights', granted: true },
          { id: 'perm_insights_export', key: 'export_insights', granted: true },
        ],
      },
      {
        permissionId: 'perm_enquiry',
        screenKey: 'enquiry',
        granted: true,
        subPermissions: [
          { id: 'perm_enquiry_view', key: 'view_enquiry', granted: true },
          { id: 'perm_enquiry_export', key: 'export_enquiry', granted: true },
        ],
      },
      {
        permissionId: 'perm_help',
        screenKey: 'help',
        granted: true,
        subPermissions: [],
      },
      {
        permissionId: 'perm_import_assets',
        screenKey: 'import_assets',
        granted: true,
        subPermissions: [],
      },
      {
        permissionId: 'perm_import_transactions',
        screenKey: 'import_transactions',
        granted: true,
        subPermissions: [],
      },
      {
        permissionId: 'perm_locations',
        screenKey: 'locations',
        granted: true,
        subPermissions: [
          { id: 'perm_locations_view', key: 'view_locations', granted: true },
          { id: 'perm_locations_manage', key: 'manage_locations', granted: true },
        ],
      },
      {
        permissionId: 'perm_categories',
        screenKey: 'categories',
        granted: true,
        subPermissions: [
          { id: 'perm_categories_view', key: 'view_categories', granted: true },
          { id: 'perm_categories_manage', key: 'manage_categories', granted: true },
        ],
      },
      {
        permissionId: 'perm_equipment',
        screenKey: 'equipment',
        granted: true,
        subPermissions: [
          { id: 'perm_equipment_view', key: 'view_equipment', granted: true },
          { id: 'perm_equipment_manage', key: 'manage_equipment', granted: true },
        ],
      },
      {
        permissionId: 'perm_disposal_reasons',
        screenKey: 'disposal_reasons',
        granted: true,
        subPermissions: [
          { id: 'perm_disposal_reasons_view', key: 'view_disposal_reasons', granted: true },
          { id: 'perm_disposal_reasons_manage', key: 'manage_disposal_reasons', granted: true },
        ],
      },
      {
        permissionId: 'perm_team_settings',
        screenKey: 'team_settings',
        granted: true,
        subPermissions: [
          { id: 'perm_team_settings_view', key: 'view_teams', granted: true },
          { id: 'perm_team_settings_manage', key: 'manage_teams', granted: true },
          { id: 'perm_team_settings_permissions', key: 'manage_permissions', granted: true },
        ],
      },
      {
        permissionId: 'perm_feature_settings',
        screenKey: 'feature_settings',
        granted: true,
        subPermissions: [
          { id: 'perm_feature_settings_view', key: 'view_features', granted: true },
          { id: 'perm_feature_settings_manage', key: 'manage_features', granted: true },
        ],
      },
      {
        permissionId: 'perm_asset_options',
        screenKey: 'asset_options',
        granted: true,
        subPermissions: [
          { id: 'perm_asset_options_view', key: 'view_asset_options', granted: true },
          { id: 'perm_asset_options_manage', key: 'manage_asset_options', granted: true },
        ],
      },
    ],
    createdAt: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00Z',
    updatedBy: 'system',
  },
  {
    id: 'team_default_admin',
    name: 'Administrators',
    icon: 'shield',
    color: '#7C3AED',
    description: 'Application administrators with full system access',
    isDefault: true,
    canDelete: false,
    canRename: false,
    memberCount: 5,
    permissions: [
      {
        permissionId: 'perm_dashboard',
        screenKey: 'dashboard',
        granted: true,
        subPermissions: [
          { key: 'view_dashboard', granted: true },
          { key: 'export_reports', granted: true },
        ],
      },
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: true },
          { key: 'create_assets', granted: true },
          { key: 'edit_basic', granted: true },
          { key: 'delete_assets', granted: true },
        ],
      },
      {
        permissionId: 'perm_admin',
        screenKey: 'user_administration',
        granted: true,
        subPermissions: [
          { key: 'view_teams', granted: true },
          { key: 'manage_teams', granted: true },
          { key: 'assign_users', granted: true },
        ],
      },
    ],
    createdAt: '2024-01-01T00:00:00Z',
    createdBy: 'system',
    updatedAt: '2024-01-01T00:00:00Z',
    updatedBy: 'system',
  },
  {
    id: 'team_engineering',
    name: 'Engineering Team',
    icon: 'code',
    color: '#3B82F6',
    description: 'Software development and infrastructure team',
    isDefault: false,
    canDelete: true,
    canRename: true,
    memberCount: 18,
    permissions: [
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: true },
          { key: 'create_assets', granted: true },
          { key: 'edit_basic', granted: true },
          { key: 'delete_assets', granted: false },
        ],
      },
    ],
    createdAt: '2024-02-15T10:30:00Z',
    createdBy: 'admin_user_001',
    updatedAt: '2024-03-20T14:22:00Z',
    updatedBy: 'admin_user_045',
  },
  {
    id: 'team_finance',
    name: 'Finance Team',
    icon: 'package',
    color: '#10B981',
    description: 'Financial management and asset accounting',
    isDefault: false,
    canDelete: true,
    canRename: true,
    memberCount: 12,
    permissions: [
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: true },
          { key: 'create_assets', granted: false },
          { key: 'edit_basic', granted: true },
          { key: 'delete_assets', granted: false },
        ],
      },
    ],
    createdAt: '2024-02-10T09:15:00Z',
    createdBy: 'admin_user_001',
    updatedAt: '2024-03-18T11:45:00Z',
    updatedBy: 'admin_user_001',
  },
  {
    id: 'team_operations',
    name: 'Operations Team',
    icon: 'settings',
    color: '#F59E0B',
    description: 'Day-to-day operations and asset management',
    isDefault: false,
    canDelete: true,
    canRename: true,
    memberCount: 24,
    permissions: [
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: true },
          { key: 'create_assets', granted: true },
          { key: 'edit_basic', granted: true },
          { key: 'delete_assets', granted: false },
        ],
      },
    ],
    createdAt: '2024-01-20T14:00:00Z',
    createdBy: 'admin_user_001',
    updatedAt: '2024-03-22T09:30:00Z',
    updatedBy: 'admin_user_045',
  },
  {
    id: 'team_auditors',
    name: 'Auditors',
    icon: 'shield-check',
    color: '#EF4444',
    description: 'Compliance and audit team with read-only access',
    isDefault: false,
    canDelete: true,
    canRename: true,
    memberCount: 6,
    permissions: [
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: true },
          { key: 'create_assets', granted: false },
          { key: 'edit_basic', granted: false },
          { key: 'delete_assets', granted: false },
        ],
      },
    ],
    createdAt: '2024-03-01T08:00:00Z',
    createdBy: 'admin_user_001',
    updatedAt: '2024-03-15T16:20:00Z',
    updatedBy: 'admin_user_001',
  },
  {
    id: 'team_maintenance',
    name: 'Maintenance Team',
    icon: 'wrench',
    color: '#8B5CF6',
    description: 'Equipment maintenance and repair team',
    isDefault: false,
    canDelete: true,
    canRename: true,
    memberCount: 0,
    permissions: [
      {
        permissionId: 'perm_assets',
        screenKey: 'asset_registry',
        granted: true,
        subPermissions: [
          { key: 'view_details', granted: true },
          { key: 'view_history', granted: true },
          { key: 'export_list', granted: false },
          { key: 'create_assets', granted: false },
          { key: 'edit_basic', granted: true },
          { key: 'delete_assets', granted: false },
        ],
      },
    ],
    createdAt: '2024-03-10T10:00:00Z',
    createdBy: 'admin_user_045',
    updatedAt: '2024-03-10T10:00:00Z',
    updatedBy: 'admin_user_045',
  },
];

// Demo Users
export const demoUsers: User[] = [
  {
    id: 'user_001',
    externalId: 'azure_abc123',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    status: 'active',
    lastLogin: '2024-03-22T09:15:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_default_admin', 'team_engineering'],
    createdAt: '2023-06-10T08:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_002',
    externalId: 'azure_def456',
    name: 'Michael Chen',
    email: 'michael.chen@company.com',
    status: 'active',
    lastLogin: '2024-03-22T08:45:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_default_admin', 'team_finance'],
    createdAt: '2023-06-10T08:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_003',
    externalId: 'azure_ghi789',
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@company.com',
    status: 'active',
    lastLogin: '2024-03-21T16:30:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_operations', 'team_engineering'],
    createdAt: '2023-07-15T09:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_004',
    externalId: 'azure_jkl012',
    name: 'David Kim',
    email: 'david.kim@company.com',
    status: 'active',
    lastLogin: '2024-03-22T07:20:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_finance'],
    createdAt: '2023-08-01T10:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_005',
    externalId: 'azure_mno345',
    name: 'Jessica Taylor',
    email: 'jessica.taylor@company.com',
    status: 'active',
    lastLogin: '2024-03-20T14:15:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_auditors'],
    createdAt: '2023-09-10T11:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_006',
    externalId: 'azure_pqr678',
    name: 'Robert Martinez',
    email: 'robert.martinez@company.com',
    status: 'inactive',
    lastLogin: '2024-02-15T10:00:00Z',
    avatarUrl: null,
    teams: ['team_default_standard'],
    createdAt: '2023-05-20T08:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_007',
    externalId: 'azure_stu901',
    name: 'Amanda White',
    email: 'amanda.white@company.com',
    status: 'active',
    lastLogin: '2024-03-22T10:00:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_operations'],
    createdAt: '2023-10-05T09:00:00Z',
    source: 'Azure AD',
  },
  {
    id: 'user_008',
    externalId: 'azure_vwx234',
    name: 'James Anderson',
    email: 'james.anderson@company.com',
    status: 'active',
    lastLogin: '2024-03-21T15:45:00Z',
    avatarUrl: null,
    teams: ['team_default_standard', 'team_engineering', 'team_operations'],
    createdAt: '2023-11-12T10:00:00Z',
    source: 'Azure AD',
  },
];

// Permission Definitions - Organized by Navigation Groups
// Updated to match new permission structure requirements
export const demoPermissions: Permission[] = [
  // Dashboard - No sub permissions
  {
    id: 'perm_dashboard',
    screenKey: 'dashboard',
    displayName: 'Dashboard',
    description: 'Access to dashboard and analytics features',
    category: 'Dashboard',
    categoryIcon: 'layout-dashboard',
    parentPermissionId: null,
    sortOrder: 1,
    subPermissions: [],
  },

  // Assets - Removed Export Assets sub permission
  {
    id: 'perm_assets',
    screenKey: 'assets',
    displayName: 'Assets',
    description: 'View and manage asset inventory',
    category: 'Assets',
    categoryIcon: 'package',
    parentPermissionId: null,
    sortOrder: 2,
    subPermissions: [
      {
        id: 'perm_assets_view',
        key: 'view_assets',
        displayName: 'View Assets',
        description: 'View asset list and details',
        parentPermissionId: 'perm_assets',
        sortOrder: 1,
      },
      {
        id: 'perm_assets_create',
        key: 'create_assets',
        displayName: 'Create Assets',
        description: 'Add new assets to the registry',
        parentPermissionId: 'perm_assets',
        sortOrder: 2,
      },
      {
        id: 'perm_assets_edit',
        key: 'edit_assets',
        displayName: 'Edit Assets',
        description: 'Modify asset information',
        parentPermissionId: 'perm_assets',
        sortOrder: 3,
      },
      {
        id: 'perm_assets_delete',
        key: 'delete_assets',
        displayName: 'Delete Assets',
        description: 'Remove assets from the system',
        parentPermissionId: 'perm_assets',
        sortOrder: 4,
      },
    ],
  },

  // Transactions - Additions (Keep all sub permissions)
  {
    id: 'perm_trans_additions',
    screenKey: 'additions',
    displayName: 'Additions',
    description: 'Manage asset additions and acquisitions',
    category: 'Transactions',
    categoryIcon: 'arrow-right-left',
    parentPermissionId: null,
    sortOrder: 3,
    subPermissions: [
      {
        id: 'perm_trans_additions_view',
        key: 'view_additions',
        displayName: 'View Additions',
        description: 'View addition transactions',
        parentPermissionId: 'perm_trans_additions',
        sortOrder: 1,
      },
      {
        id: 'perm_trans_additions_create',
        key: 'create_additions',
        displayName: 'Create Additions',
        description: 'Record new asset additions',
        parentPermissionId: 'perm_trans_additions',
        sortOrder: 2,
      },
      {
        id: 'perm_trans_additions_edit',
        key: 'edit_additions',
        displayName: 'Edit Additions',
        description: 'Modify addition transactions',
        parentPermissionId: 'perm_trans_additions',
        sortOrder: 3,
      },
    ],
  },

  // Depreciation - Updated with View, Edit, Create sub permissions like Additions
  {
    id: 'perm_trans_depreciation',
    screenKey: 'depreciation',
    displayName: 'Depreciation',
    description: 'Manage asset depreciation',
    category: 'Transactions',
    categoryIcon: 'arrow-right-left',
    parentPermissionId: null,
    sortOrder: 4,
    subPermissions: [
      {
        id: 'perm_trans_depreciation_view',
        key: 'view_depreciation',
        displayName: 'View Depreciation',
        description: 'View depreciation schedules and batches',
        parentPermissionId: 'perm_trans_depreciation',
        sortOrder: 1,
      },
      {
        id: 'perm_trans_depreciation_create',
        key: 'create_depreciation',
        displayName: 'Create Depreciation',
        description: 'Create new depreciation batches',
        parentPermissionId: 'perm_trans_depreciation',
        sortOrder: 2,
      },
      {
        id: 'perm_trans_depreciation_edit',
        key: 'edit_depreciation',
        displayName: 'Edit Depreciation',
        description: 'Modify depreciation batches',
        parentPermissionId: 'perm_trans_depreciation',
        sortOrder: 3,
      },
    ],
  },

  // Revaluation - Updated with View, Edit, Create sub permissions like Additions
  {
    id: 'perm_trans_revaluation',
    screenKey: 'revaluation',
    displayName: 'Revaluation',
    description: 'Manage asset revaluations',
    category: 'Transactions',
    categoryIcon: 'arrow-right-left',
    parentPermissionId: null,
    sortOrder: 5,
    subPermissions: [
      {
        id: 'perm_trans_revaluation_view',
        key: 'view_revaluation',
        displayName: 'View Revaluations',
        description: 'View revaluation records',
        parentPermissionId: 'perm_trans_revaluation',
        sortOrder: 1,
      },
      {
        id: 'perm_trans_revaluation_create',
        key: 'create_revaluation',
        displayName: 'Create Revaluations',
        description: 'Record asset revaluations',
        parentPermissionId: 'perm_trans_revaluation',
        sortOrder: 2,
      },
      {
        id: 'perm_trans_revaluation_edit',
        key: 'edit_revaluation',
        displayName: 'Edit Revaluations',
        description: 'Modify revaluation records',
        parentPermissionId: 'perm_trans_revaluation',
        sortOrder: 3,
      },
    ],
  },

  // Disposals - Manage asset disposals
  {
    id: 'perm_trans_disposals',
    screenKey: 'disposals',
    displayName: 'Disposals',
    description: 'Manage asset disposals',
    category: 'Transactions',
    categoryIcon: 'arrow-right-left',
    parentPermissionId: null,
    sortOrder: 6,
    subPermissions: [
      {
        id: 'perm_trans_disposals_view',
        key: 'view_disposals',
        displayName: 'View Disposals',
        description: 'View disposal records',
        parentPermissionId: 'perm_trans_disposals',
        sortOrder: 1,
      },
      {
        id: 'perm_trans_disposals_create',
        key: 'create_disposals',
        displayName: 'Create Disposals',
        description: 'Record asset disposals',
        parentPermissionId: 'perm_trans_disposals',
        sortOrder: 2,
      },
      {
        id: 'perm_trans_disposals_edit',
        key: 'edit_disposals',
        displayName: 'Edit Disposals',
        description: 'Modify disposal records',
        parentPermissionId: 'perm_trans_disposals',
        sortOrder: 3,
      },
    ],
  },

  // Tasks - Updated with new sub permissions
  {
    id: 'perm_tasks',
    screenKey: 'tasks',
    displayName: 'Tasks',
    description: 'Manage tasks and workflows',
    category: 'Tasks',
    categoryIcon: 'check-square',
    parentPermissionId: null,
    sortOrder: 7,
    subPermissions: [
      {
        id: 'perm_tasks_view',
        key: 'view_tasks',
        displayName: 'View Tasks',
        description: 'View your assigned tasks',
        parentPermissionId: 'perm_tasks',
        sortOrder: 1,
      },
      {
        id: 'perm_tasks_create',
        key: 'create_tasks',
        displayName: 'Create Tasks',
        description: 'Create new tasks',
        parentPermissionId: 'perm_tasks',
        sortOrder: 2,
      },
      {
        id: 'perm_tasks_edit',
        key: 'edit_tasks',
        displayName: 'Edit Tasks',
        description: 'Edit and complete your own tasks',
        parentPermissionId: 'perm_tasks',
        sortOrder: 3,
      },
      {
        id: 'perm_tasks_assign',
        key: 'assign_tasks',
        displayName: 'Assign Tasks',
        description: 'Assign tasks to other users',
        parentPermissionId: 'perm_tasks',
        sortOrder: 4,
      },
      {
        id: 'perm_tasks_admin',
        key: 'task_administrator',
        displayName: 'Task Administrator',
        description: 'View, edit and complete other users\' tasks',
        parentPermissionId: 'perm_tasks',
        sortOrder: 5,
      },
    ],
  },

  // Audits - Auditor role (carries out audits in mobile app)
  {
    id: 'perm_audits_auditor',
    screenKey: 'audits-auditor',
    displayName: 'Audit Execution',
    description: 'Carry out audits in the mobile app',
    category: 'Audits',
    categoryIcon: 'clipboard-check',
    parentPermissionId: null,
    sortOrder: 7,
    subPermissions: [
      {
        id: 'perm_audits_auditor_view',
        key: 'view_assigned_audits',
        displayName: 'View Assigned Audits',
        description: 'View audits assigned to you',
        parentPermissionId: 'perm_audits_auditor',
        sortOrder: 1,
      },
      {
        id: 'perm_audits_auditor_carry_out',
        key: 'carry_out_audit',
        displayName: 'Carry Out Audit',
        description: 'Perform audit checks on assets',
        parentPermissionId: 'perm_audits_auditor',
        sortOrder: 2,
      },
      {
        id: 'perm_audits_auditor_process',
        key: 'process_audit',
        displayName: 'Process Audit',
        description: 'Submit and process completed audits',
        parentPermissionId: 'perm_audits_auditor',
        sortOrder: 3,
      },
    ],
  },

  // Audits - Manager role (manages audits in main application)
  {
    id: 'perm_audits_manager',
    screenKey: 'audits-manager',
    displayName: 'Audit Management',
    description: 'Manage audits in the main application',
    category: 'Audits',
    categoryIcon: 'clipboard-list',
    parentPermissionId: null,
    sortOrder: 8,
    subPermissions: [
      {
        id: 'perm_audits_manager_view',
        key: 'view_all_audits',
        displayName: 'View All Audits',
        description: 'View all audits in the system',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 1,
      },
      {
        id: 'perm_audits_manager_create',
        key: 'create_audits',
        displayName: 'Create Audits',
        description: 'Create new audit schedules',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 2,
      },
      {
        id: 'perm_audits_manager_edit',
        key: 'edit_audits',
        displayName: 'Edit Audits',
        description: 'Edit existing audits',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 3,
      },
      {
        id: 'perm_audits_manager_flag',
        key: 'flag_audits',
        displayName: 'Flag Audits',
        description: 'Flag audits for review',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 4,
      },
      {
        id: 'perm_audits_manager_comments',
        key: 'raise_comments',
        displayName: 'Raise Comments',
        description: 'Add comments to audits',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 5,
      },
      {
        id: 'perm_audits_manager_work_items',
        key: 'raise_work_items',
        displayName: 'Raise Work Items',
        description: 'Create work items from audit findings',
        parentPermissionId: 'perm_audits_manager',
        sortOrder: 6,
      },
    ],
  },

  // Audit Trail - No sub permissions
  {
    id: 'perm_audit_trail',
    screenKey: 'audit-trail',
    displayName: 'Audit Trail',
    description: 'View system audit trail and history',
    category: 'Reports',
    categoryIcon: 'history',
    parentPermissionId: null,
    sortOrder: 9,
    subPermissions: [],
  },

  // Compliance - Manage compliance requirements
  {
    id: 'perm_compliance',
    screenKey: 'compliance',
    displayName: 'Compliance',
    description: 'Manage compliance requirements and schedules',
    category: 'Compliance',
    categoryIcon: 'shield',
    parentPermissionId: null,
    sortOrder: 10,
    subPermissions: [
      {
        id: 'perm_compliance_view',
        key: 'view_compliance',
        displayName: 'View Compliance',
        description: 'View compliance dashboard and status',
        parentPermissionId: 'perm_compliance',
        sortOrder: 1,
      },
      {
        id: 'perm_compliance_manage',
        key: 'manage_compliance',
        displayName: 'Manage Compliance',
        description: 'Configure compliance rules and schedules',
        parentPermissionId: 'perm_compliance',
        sortOrder: 2,
      },
    ],
  },

  // Check-In/Check-Out - Asset booking and tracking
  {
    id: 'perm_checkinout',
    screenKey: 'checkinout',
    displayName: 'Check-In / Check-Out',
    description: 'Manage asset bookings and check-in/check-out',
    category: 'Management',
    categoryIcon: 'calendar-check',
    parentPermissionId: null,
    sortOrder: 11,
    subPermissions: [
      {
        id: 'perm_checkinout_view',
        key: 'view_checkinout',
        displayName: 'View Bookings',
        description: 'View asset bookings and availability',
        parentPermissionId: 'perm_checkinout',
        sortOrder: 1,
      },
      {
        id: 'perm_checkinout_manage',
        key: 'manage_checkinout',
        displayName: 'Manage Bookings',
        description: 'Create and manage asset bookings',
        parentPermissionId: 'perm_checkinout',
        sortOrder: 2,
      },
    ],
  },

  // Insights - Reports and analytics
  {
    id: 'perm_insights',
    screenKey: 'insights',
    displayName: 'Insights',
    description: 'View reports and analytics',
    category: 'Reports',
    categoryIcon: 'bar-chart',
    parentPermissionId: null,
    sortOrder: 12,
    subPermissions: [
      {
        id: 'perm_insights_view',
        key: 'view_insights',
        displayName: 'View Insights',
        description: 'View reports and dashboards',
        parentPermissionId: 'perm_insights',
        sortOrder: 1,
      },
      {
        id: 'perm_insights_export',
        key: 'export_insights',
        displayName: 'Export Reports',
        description: 'Export reports and data',
        parentPermissionId: 'perm_insights',
        sortOrder: 2,
      },
    ],
  },

  // Enquiry - Asset history and transaction enquiry
  {
    id: 'perm_enquiry',
    screenKey: 'enquiry',
    displayName: 'Transaction History',
    description: 'View asset transaction history and enquiry',
    category: 'Reports',
    categoryIcon: 'search',
    parentPermissionId: null,
    sortOrder: 13,
    subPermissions: [
      {
        id: 'perm_enquiry_view',
        key: 'view_enquiry',
        displayName: 'View History',
        description: 'View asset transaction history',
        parentPermissionId: 'perm_enquiry',
        sortOrder: 1,
      },
      {
        id: 'perm_enquiry_export',
        key: 'export_enquiry',
        displayName: 'Export History',
        description: 'Export transaction history data',
        parentPermissionId: 'perm_enquiry',
        sortOrder: 2,
      },
    ],
  },

  // Help - Help and support
  {
    id: 'perm_help',
    screenKey: 'help',
    displayName: 'Help & Support',
    description: 'Access help documentation and support',
    category: 'Help',
    categoryIcon: 'help-circle',
    parentPermissionId: null,
    sortOrder: 14,
    subPermissions: [],
  },

  // Import Assets - No sub permissions
  {
    id: 'perm_import_assets',
    screenKey: 'import-assets',
    displayName: 'Import Assets',
    description: 'Import asset data from files',
    category: 'Import',
    categoryIcon: 'upload',
    parentPermissionId: null,
    sortOrder: 10,
    subPermissions: [],
  },

  // Import Transactions - No sub permissions
  {
    id: 'perm_import_transactions',
    screenKey: 'import-transactions',
    displayName: 'Import Transactions',
    description: 'Import transaction data from files',
    category: 'Import',
    categoryIcon: 'file-up',
    parentPermissionId: null,
    sortOrder: 11,
    subPermissions: [],
  },

  // Configuration - Keep current sub permissions
  {
    id: 'perm_locations',
    screenKey: 'locations',
    displayName: 'Locations',
    description: 'Manage location master data',
    category: 'Configuration',
    categoryIcon: 'sliders',
    parentPermissionId: null,
    sortOrder: 12,
    subPermissions: [
      {
        id: 'perm_locations_view',
        key: 'view_locations',
        displayName: 'View Locations',
        description: 'View location list',
        parentPermissionId: 'perm_locations',
        sortOrder: 1,
      },
      {
        id: 'perm_locations_manage',
        key: 'manage_locations',
        displayName: 'Manage Locations',
        description: 'Create, edit, and delete locations',
        parentPermissionId: 'perm_locations',
        sortOrder: 2,
      },
    ],
  },
  {
    id: 'perm_categories',
    screenKey: 'categories',
    displayName: 'Categories',
    description: 'Manage asset categories',
    category: 'Configuration',
    categoryIcon: 'sliders',
    parentPermissionId: null,
    sortOrder: 13,
    subPermissions: [
      {
        id: 'perm_categories_view',
        key: 'view_categories',
        displayName: 'View Categories',
        description: 'View category list',
        parentPermissionId: 'perm_categories',
        sortOrder: 1,
      },
      {
        id: 'perm_categories_manage',
        key: 'manage_categories',
        displayName: 'Manage Categories',
        description: 'Create, edit, and delete categories',
        parentPermissionId: 'perm_categories',
        sortOrder: 2,
      },
    ],
  },
  {
    id: 'perm_equipment',
    screenKey: 'equipment',
    displayName: 'Equipment',
    description: 'Manage equipment types',
    category: 'Configuration',
    categoryIcon: 'sliders',
    parentPermissionId: null,
    sortOrder: 14,
    subPermissions: [
      {
        id: 'perm_equipment_view',
        key: 'view_equipment',
        displayName: 'View Equipment',
        description: 'View equipment types',
        parentPermissionId: 'perm_equipment',
        sortOrder: 1,
      },
      {
        id: 'perm_equipment_manage',
        key: 'manage_equipment',
        displayName: 'Manage Equipment',
        description: 'Create, edit, and delete equipment types',
        parentPermissionId: 'perm_equipment',
        sortOrder: 2,
      },
    ],
  },
  {
    id: 'perm_disposal_reasons',
    screenKey: 'disposal-reasons',
    displayName: 'Disposal Reasons',
    description: 'Manage disposal reason codes',
    category: 'Configuration',
    categoryIcon: 'sliders',
    parentPermissionId: null,
    sortOrder: 15,
    subPermissions: [
      {
        id: 'perm_disposal_reasons_view',
        key: 'view_disposal_reasons',
        displayName: 'View Reasons',
        description: 'View disposal reasons',
        parentPermissionId: 'perm_disposal_reasons',
        sortOrder: 1,
      },
      {
        id: 'perm_disposal_reasons_manage',
        key: 'manage_disposal_reasons',
        displayName: 'Manage Reasons',
        description: 'Create, edit, and delete disposal reasons',
        parentPermissionId: 'perm_disposal_reasons',
        sortOrder: 2,
      },
    ],
  },

  // Settings - Keep existing permissions
  {
    id: 'perm_team_settings',
    screenKey: 'team-settings',
    displayName: 'Team Settings',
    description: 'Manage teams and permissions',
    category: 'Settings',
    categoryIcon: 'settings',
    parentPermissionId: null,
    sortOrder: 16,
    subPermissions: [
      {
        id: 'perm_team_settings_view',
        key: 'view_teams',
        displayName: 'View Teams',
        description: 'View team and user information',
        parentPermissionId: 'perm_team_settings',
        sortOrder: 1,
      },
      {
        id: 'perm_team_settings_manage',
        key: 'manage_teams',
        displayName: 'Manage Teams',
        description: 'Create, edit, and delete teams',
        parentPermissionId: 'perm_team_settings',
        sortOrder: 2,
      },
      {
        id: 'perm_team_settings_permissions',
        key: 'manage_permissions',
        displayName: 'Manage Permissions',
        description: 'Configure team permissions',
        parentPermissionId: 'perm_team_settings',
        sortOrder: 3,
      },
    ],
  },
  {
    id: 'perm_feature_settings',
    screenKey: 'feature-settings',
    displayName: 'Feature Settings',
    description: 'Configure application features',
    category: 'Settings',
    categoryIcon: 'settings',
    parentPermissionId: null,
    sortOrder: 17,
    subPermissions: [
      {
        id: 'perm_feature_settings_view',
        key: 'view_features',
        displayName: 'View Features',
        description: 'View feature configuration',
        parentPermissionId: 'perm_feature_settings',
        sortOrder: 1,
      },
      {
        id: 'perm_feature_settings_manage',
        key: 'manage_features',
        displayName: 'Manage Features',
        description: 'Enable and configure features',
        parentPermissionId: 'perm_feature_settings',
        sortOrder: 2,
      },
    ],
  },
  {
    id: 'perm_asset_options',
    screenKey: 'asset-options',
    displayName: 'Asset Options',
    description: 'Configure asset-related settings',
    category: 'Settings',
    categoryIcon: 'settings',
    parentPermissionId: null,
    sortOrder: 18,
    subPermissions: [
      {
        id: 'perm_asset_options_view',
        key: 'view_asset_options',
        displayName: 'View Options',
        description: 'View asset configuration',
        parentPermissionId: 'perm_asset_options',
        sortOrder: 1,
      },
      {
        id: 'perm_asset_options_manage',
        key: 'manage_asset_options',
        displayName: 'Manage Options',
        description: 'Configure asset settings',
        parentPermissionId: 'perm_asset_options',
        sortOrder: 2,
      },
    ],
  },
];
